/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { HTMLAttributes, useState, useEffect } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { Coupons as CouponsComponent } from '@/cart/components';
import { Button, Input, Icon, Tag } from '@adobe-commerce/elsie/components';
import { Close } from '@adobe-commerce/elsie/icons';
import { applyCouponsToCart, ApplyCouponsStrategy } from '@/cart/api';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface CouponsProps extends HTMLAttributes<HTMLDivElement> {}

export const Coupons: Container<CouponsProps> = ({ children, ...props }) => {
  const [successfulCouponSet, setSuccessfulCouponSet] = useState(new Set());
  const [appliedCouponsArray, setAppliedCouponsArray] = useState<string[]>([]);
  const [couponError, setCouponError] = useState(new Set());

  const dictionary = useText({
    applyButton: 'Cart.PriceSummary.coupon.applyAction',
    applyButtonAriaLabel: 'Cart.PriceSummary.coupon.applyActionAriaLabel',
    placeholder: 'Cart.PriceSummary.coupon.placeholder',
    ariaLabelRemove: 'Cart.PriceSummary.coupon.ariaLabelRemove',
  });

  const handleApplyCoupon = async (value: { coupon: string }) => {
    const couponCode = value.coupon;

    const newSet = new Set(successfulCouponSet);
    newSet.add(couponCode);
    setCouponError(new Set());

    const couponCodes: string[] = Array.from(newSet) as string[];
    applyCouponsToCart(couponCodes, ApplyCouponsStrategy.REPLACE)
      .then((result) => {
        if (result === null) {
          throw new Error('Error adding coupon code');
        }

        setSuccessfulCouponSet(newSet);
      })
      .catch((error) => {
        console.warn(error);
        setCouponError(new Set([error.message]));
      });
  };

  const handleRemoveCoupon = (couponCode: string) => {
    const newSet = new Set(successfulCouponSet);
    newSet.delete(couponCode);
    setCouponError(new Set());

    const couponCodes: string[] = Array.from(newSet) as string[];
    applyCouponsToCart(couponCodes, ApplyCouponsStrategy.REPLACE)
      .then((result) => {
        if (result === null) {
          throw new Error('Error removing coupon code');
        }

        setSuccessfulCouponSet(newSet);
      })
      .catch((error) => {
        console.warn(error);
        setCouponError(new Set([error.message]));
      });
  };

  useEffect(() => {
    const cartEvent = events.on(
      'cart/data',
      (payload) => {
        const appliedCoupons = payload?.appliedCoupons;

        if (!appliedCoupons) {
          // When appliedCoupons returns null, no coupons are applied
          setAppliedCouponsArray([]);
          // Clear error message
          setCouponError(new Set());
          return;
        }

        const couponCodes = appliedCoupons.map((coupon) => coupon.code);

        setAppliedCouponsArray(couponCodes);
        // Clear error message
        setCouponError(new Set());
      },
      { eager: true }
    );

    return () => {
      cartEvent?.off();
    };
  }, []);

  useEffect(() => {
    setSuccessfulCouponSet(new Set(appliedCouponsArray));
  }, [appliedCouponsArray]);

  useEffect(() => {
    const cartEvent = events.on(
      'shipping/estimate',
      () => {
        setCouponError(new Set());
      },
      { eager: true }
    );

    return () => {
      cartEvent?.off();
    };
  }, []);

  const appliedCoupons = appliedCouponsArray.map((code, index) => (
    <Tag key={code} className="coupon-code-form__applied-item">
      <span>{code}</span>
      <button
        aria-label={`${dictionary.ariaLabelRemove} ${code}`}
        onClick={() => handleRemoveCoupon(code)}
        data-testid={`remove-coupon-${index + 1}`}
      >
        <Icon source={Close} size={'16'} />
      </button>
    </Tag>
  ));

  const errorMessage =
    couponError.size > 0 ? (
      <div data-testid="coupon-code-error">
        {Array.from(couponError)[0] as string}
      </div>
    ) : undefined;

  return (
    <CouponsComponent
      {...props}
      couponCodeField={
        <Input
          aria-label={dictionary.placeholder}
          type="text"
          placeholder={dictionary.placeholder}
          name="coupon"
          variant="primary"
          value=""
          data-testid="coupon-code-input"
          maxLength={50}
          error={couponError.size > 0}
        />
      }
      applyCouponsButton={
        <Button
          variant="secondary"
          aria-label={dictionary.applyButtonAriaLabel}
        >
          {dictionary.applyButton}
        </Button>
      }
      error={errorMessage}
      appliedCoupons={<div>{appliedCoupons}</div>}
      onApplyCoupon={handleApplyCoupon}
    />
  );
};
